import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Loyalty member points + tier-perk redemption UI, extracted from
 * PaymentModal.jsx (was ~130 lines inline).
 *
 * Rendered inside the Pay wizard's TIP step. Emits perk selection and
 * point-redemption changes to the parent, which recomputes the
 * discount + reruns the tender calculations.
 */
const LoyaltyMemberPanel = ({
  member,
  subtotal,
  redeemPoints,
  onRedeemChange,
  perks = { info_perks: [], redeemable_perks: [] },
  selectedPerk,
  onSelectPerk,
}) => {
  const maxRedeemable = Math.min(member.points, Math.floor(subtotal * 100));
  const tierColors = {
    bronze: "text-amber-700",
    silver: "text-slate-500",
    gold: "text-yellow-600",
    platinum: "text-purple-600",
  };
  const presets = useMemo(() => {
    const opts = [];
    if (member.points >= 100) opts.push(100);
    if (member.points >= 500) opts.push(500);
    if (member.points >= 1000) opts.push(1000);
    if (member.points >= 100 && member.points <= maxRedeemable) opts.push(member.points);
    return Array.from(new Set(opts)).filter((p) => p <= maxRedeemable).slice(0, 4);
  }, [member.points, maxRedeemable]);

  const perkDiscountText = (p) =>
    p.type === "percent" ? `-${p.value}%` : `-$${(p.value || 0).toFixed(2)}`;

  return (
    <div className="rounded-lg border border-border p-4 space-y-3" data-testid="loyalty-member-panel">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Welcome back</div>
          <div className="font-medium text-lg">{member.name}</div>
        </div>
        <div className={`text-sm font-semibold uppercase ${tierColors[member.tier] || ""}`}>{member.tier}</div>
      </div>
      <div className="flex items-baseline justify-between">
        <div className="text-3xl font-mono font-semibold" data-testid="loyalty-points">{member.points.toLocaleString()}</div>
        <div className="text-xs text-muted-foreground">points available</div>
      </div>

      {(perks.info_perks.length > 0 || perks.redeemable_perks.length > 0) && (
        <div className="space-y-2 pt-2 border-t border-border/50" data-testid="loyalty-perks">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Tier Perks</div>
          {perks.info_perks.map((p) => (
            <div key={p.id} className="text-xs text-muted-foreground flex items-center gap-1">
              <span className="text-accent">•</span> {p.label}
            </div>
          ))}
          {perks.redeemable_perks.length > 0 && (
            <div className="grid grid-cols-1 gap-1">
              {perks.redeemable_perks.map((p) => {
                const active = selectedPerk?.id === p.id;
                const cooling = (p.cooldown_remaining_days || 0) > 0;
                const disabled = cooling && !active;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { if (!disabled) onSelectPerk(active ? null : p); }}
                    disabled={disabled}
                    className={`text-left rounded-md border px-3 py-2 text-sm transition-colors ${active ? "border-accent bg-accent/5" : disabled ? "border-border/60 bg-muted/40 opacity-60 cursor-not-allowed" : "border-border hover:border-muted-foreground/40"}`}
                    data-testid={`perk-redeem-${p.id}`}
                    title={cooling ? `On cooldown for ${p.cooldown_remaining_days} more day${p.cooldown_remaining_days === 1 ? "" : "s"}` : undefined}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{p.label}</span>
                      <span className="font-mono text-xs opacity-80">{perkDiscountText(p)}</span>
                    </div>
                    {cooling && (
                      <div className="text-xs text-muted-foreground mt-0.5" data-testid={`perk-cooldown-${p.id}`}>
                        Available in {p.cooldown_remaining_days} day{p.cooldown_remaining_days === 1 ? "" : "s"}
                      </div>
                    )}
                    {active && <div className="text-xs text-accent mt-0.5">Applied — tap again to remove</div>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="pt-2 border-t border-border/50">
        <div className="text-xs text-muted-foreground mb-1">100 points = $1.00 off</div>
        <div className="grid grid-cols-4 gap-2 mb-2">
          <Button
            variant={redeemPoints === 0 ? "default" : "outline"}
            size="sm"
            onClick={() => onRedeemChange(0)}
            className="text-xs"
            data-testid="loyalty-redeem-none"
          >
            None
          </Button>
          {presets.map((p) => (
            <Button
              key={p}
              variant={redeemPoints === p ? "default" : "outline"}
              size="sm"
              onClick={() => onRedeemChange(p)}
              className="text-xs flex-col h-auto py-1"
              data-testid={`loyalty-redeem-${p}`}
            >
              <span>{p}</span>
              <span className="opacity-75 font-mono">−${(p / 100).toFixed(2)}</span>
            </Button>
          ))}
        </div>
        <Input
          type="number"
          placeholder="Or type a custom amount"
          value={redeemPoints === 0 ? "" : redeemPoints}
          min={0}
          max={maxRedeemable}
          onChange={(e) => {
            const v = Math.max(0, Math.min(maxRedeemable, parseInt(e.target.value, 10) || 0));
            onRedeemChange(v);
          }}
          className="font-mono"
          data-testid="loyalty-redeem-input"
        />
      </div>
    </div>
  );
};

export default LoyaltyMemberPanel;
